import { PITCH, TICK_MS } from '../core/constants';
import { clamp } from '../core/math';
import type { Match } from '../sim/match';
import { MatchPhase } from '../sim/referee';
import { SetPiece } from '../sim/setpiece';

/**
 * The referee carries two whistle recordings and they are not interchangeable, which is
 * easier to see in the waveform than in the file names. `whistle2.wav` is a single blast,
 * 1.23 s of one continuous note. `whistle3.wav` is three separate blasts inside its 1.46 s —
 * the peep-peep-peeep every ground in the world knows as the end of a period.
 *
 * So the single blast stops play and restarts it, and the three blasts end a half and end the
 * match. That is also exactly how the original uses them (`referee.cpp:88-89` for the end of a
 * period against `:194-195` and `:225-226` for a stoppage and a restart); this build had the
 * three blasts on every throw-in, which both wore the sound out and left nothing distinctive
 * to finish the match with.
 */
export const SOUND = {
  ball: 'media/sounds/ballsound.wav',
  woodwork: 'media/sounds/goalpost.wav',
  /** One blast: play has stopped, or play may restart. */
  shortWhistle: 'media/sounds/whistle2.wav',
  /** Three blasts: the half is over, or the match is. */
  longWhistle: 'media/sounds/whistle3.wav',
  quietCrowd: 'media/sounds/crowd01.wav',
  loudCrowd: 'media/sounds/crowd02.wav',
} as const;

export type SoundKey = (typeof SOUND)[keyof typeof SOUND];

export interface OneShot {
  key: SoundKey;
  gain: number;
  playbackRate: number;
}

export interface AudioFrame {
  oneShots: OneShot[];
  quietCrowdGain: number;
  loudCrowdGain: number;
}

export type AudioMatchView = Pick<
  Match,
  | 'timeMs'
  | 'inPlay'
  | 'bestPossessionTeamId'
  | 'pendingRestart'
  | 'players'
  | 'teams'
  | 'ball'
  | 'phase'
  | 'gameOver'
>;

// Match exposes the score transition but not the original's transient "just scored" flag.
// Hold the same target for the visible celebration window without adding simulation state.
// The final whistle borrows it: a ground makes noise when the match ends too.
const CROWD_PEAK_MS = 5000;

/**
 * Turns successive match snapshots into source-level audio commands. It deliberately knows
 * nothing about Web Audio, so timing and gain behaviour can be tested without an audio device.
 */
export class AudioStateTracker {
  private initialized = false;
  private lastTimeMs = 0;
  private lastTouchTimeMs = 0;
  private lastWoodworkTimeMs = -1;
  private lastStopWhistleTimeMs = -1;
  private lastStartWhistleTimeMs = -1;
  private crowdPeakUntilMs = -1;
  private scores: [number, number] = [0, 0];
  private excitement = 0;
  /** The period being played, so the tick a period ends on can be recognised. */
  private phase: MatchPhase = MatchPhase.PreMatch;
  private wasOver = false;

  constructor(
    private masterVolume: number,
    private readonly random: () => number,
  ) {}

  /**
   * Turn the whole mix up or down, and hand back what the crowd should be at right now.
   *
   * The crowd is two loops whose gain is written once a frame, so a volume changed while the
   * match is paused — which is where the menu that changes it lives — would not be heard until
   * play resumed. Returning the frame lets the caller apply it on the spot.
   */
  setMasterVolume(volume: number): AudioFrame {
    this.masterVolume = clamp(volume, 0, 1);
    return this.frame([]);
  }

  /**
   * Forget the match just finished.
   *
   * A page can now play a second match without reloading, and every field here is a
   * comparison against the last tick of the last one — a clock that has gone backwards, a
   * score that has fallen to nil, a period that has started again. Left alone, the next match
   * kicks off to the roar the last one ended with and a whistle for a goal nobody scored.
   */
  reset(): void {
    this.initialized = false;
    this.lastTimeMs = 0;
    this.lastTouchTimeMs = 0;
    this.lastWoodworkTimeMs = -1;
    this.lastStopWhistleTimeMs = -1;
    this.lastStartWhistleTimeMs = -1;
    this.crowdPeakUntilMs = -1;
    this.scores = [0, 0];
    this.excitement = 0;
    this.phase = MatchPhase.PreMatch;
    this.wasOver = false;
  }

  update(match: AudioMatchView): AudioFrame {
    if (!this.initialized) {
      this.initialize(match);
      return this.frame([]);
    }

    const oneShots: OneShot[] = [];
    const scoreChanged =
      match.teams[0].score !== this.scores[0] || match.teams[1].score !== this.scores[1];
    if (scoreChanged) this.crowdPeakUntilMs = match.timeMs + CROWD_PEAK_MS;
    this.scores = [match.teams[0].score, match.teams[1].score];

    const touchTimeMs = latestTouchTime(match);
    if (touchTimeMs > this.lastTouchTimeMs) {
      const gain = touchGain(match.ball.momentum.length()) * this.masterVolume;
      if (gain >= 0.01) {
        oneShots.push({
          key: SOUND.ball,
          gain,
          playbackRate: 0.9 + clamp(this.random(), 0, 1) * 0.2,
        });
      }
      this.lastTouchTimeMs = touchTimeMs;
    }

    if (match.ball.hitWoodwork && match.timeMs !== this.lastWoodworkTimeMs) {
      oneShots.push({
        key: SOUND.woodwork,
        gain: clamp(match.ball.momentum.length() * 0.05, 0.01, 1) * 0.5 * this.masterVolume,
        playbackRate: 1,
      });
      this.lastWoodworkTimeMs = match.timeMs;
    }

    // A referee blows to STOP play when he is the one stopping it — a foul, an offside, a
    // penalty. He does not blow for a throw-in, a corner or a goal kick: the ball crossing the
    // line is self-evident and everyone in the ground can see it. Both the original
    // (`referee.cpp:194`) and this port used to whistle for all of them, which is most of why
    // the referee sounded like a man refereeing five-a-side. Restarting is a separate question
    // and still gets its pip below, because a player does need telling that the ball is live.
    const restart = match.pendingRestart;
    if (
      restart &&
      (restart.type === SetPiece.FreeKick || restart.type === SetPiece.Penalty) &&
      !restart.endPhase &&
      match.timeMs >= restart.stopTimeMs + 300 &&
      restart.stopTimeMs !== this.lastStopWhistleTimeMs
    ) {
      oneShots.push({
        key: SOUND.shortWhistle,
        gain: 0.3 * this.masterVolume,
        playbackRate: 1,
      });
      this.lastStopWhistleTimeMs = restart.stopTimeMs;
    }

    if (
      restart?.started &&
      restart.startTimeMs !== this.lastStartWhistleTimeMs
    ) {
      oneShots.push({
        key: SOUND.shortWhistle,
        gain: 0.3 * this.masterVolume,
        playbackRate: 1,
      });
      this.lastStartWhistleTimeMs = restart.startTimeMs;
    }

    // THE END OF A PERIOD, which is the one thing nothing was watching for.
    //
    // Half time announces itself by the period changing: the referee moves the match on to the
    // next one and schedules its kick-off in the same breath. Full time announces itself by
    // the match being over, and by nothing else — no restart is scheduled, which is exactly
    // why the match used to finish in silence. The two are mutually exclusive except in one
    // corner (a tie level after two periods of extra time moves on to penalties AND ends), so
    // the whistle is blown once either way.
    const periodEnded = match.phase !== this.phase && this.phase !== MatchPhase.PreMatch;
    const matchEnded = match.gameOver && !this.wasOver;
    this.phase = match.phase;
    this.wasOver = match.gameOver;
    if (matchEnded || (periodEnded && !match.gameOver)) {
      oneShots.push({
        key: SOUND.longWhistle,
        gain: 0.3 * this.masterVolume,
        playbackRate: 1,
      });
      // A ground makes a noise when the match ends, whoever won.
      if (matchEnded) this.crowdPeakUntilMs = match.timeMs + CROWD_PEAK_MS;
    }

    this.advanceExcitement(match);
    this.lastTimeMs = match.timeMs;
    return this.frame(oneShots);
  }

  private initialize(match: AudioMatchView): void {
    this.initialized = true;
    this.lastTimeMs = match.timeMs;
    this.lastTouchTimeMs = latestTouchTime(match);
    this.scores = [match.teams[0].score, match.teams[1].score];
    // Adopt whatever period the match is already in, so attaching to one in progress does not
    // read as a period having just ended.
    this.phase = match.phase;
    this.wasOver = match.gameOver;
    const restart = match.pendingRestart;
    if (restart && match.timeMs >= restart.stopTimeMs + 300) {
      this.lastStopWhistleTimeMs = restart.stopTimeMs;
    }
    if (restart?.started) this.lastStartWhistleTimeMs = restart.startTimeMs;
    if (match.ball.hitWoodwork) this.lastWoodworkTimeMs = match.timeMs;
  }

  private advanceExcitement(match: AudioMatchView): void {
    const elapsedTicks = Math.floor(Math.max(match.timeMs - this.lastTimeMs, 0) / TICK_MS);
    if (elapsedTicks === 0) return;

    const target = crowdTarget(match, match.timeMs < this.crowdPeakUntilMs);
    const perTick = target > this.excitement ? 0.02 : 0.002;
    const blend = 1 - Math.pow(1 - perTick, elapsedTicks);
    this.excitement += (target - this.excitement) * blend;
  }

  private frame(oneShots: OneShot[]): AudioFrame {
    return {
      oneShots,
      quietCrowdGain: this.excitement * 0.5 * this.masterVolume,
      loudCrowdGain:
        clamp((this.excitement - 0.3) * 1.43, 0, 1) * 0.5 * this.masterVolume,
    };
  }
}

function latestTouchTime(match: AudioMatchView): number {
  let latest = 0;
  for (const player of match.players) latest = Math.max(latest, player.lastTouchTimeMs);
  return latest;
}

function touchGain(speed: number): number {
  const normalized = clamp((speed - 4) / (40 - 4), 0, 1);
  return Math.pow(normalized, 0.7) * 0.6;
}

function crowdTarget(match: AudioMatchView, goalJustScored: boolean): number {
  if (goalJustScored) return 1;
  if (!match.inPlay) return 0.2;

  const teamId = match.bestPossessionTeamId;
  const possessionSide =
    teamId === 0 ? match.teams[0].side : teamId === 1 ? match.teams[1].side : null;
  const targetX = possessionSide === null
    ? Math.sign(match.ball.position.x || 1) * PITCH.halfLength
    : -possessionSide * PITCH.halfLength;
  const distanceToGoal = Math.hypot(targetX - match.ball.position.x, match.ball.position.y);
  return Math.pow(1 - clamp(distanceToGoal / 80, 0.3, 0.7), 1.2);
}
